import { useEffect, useState } from 'react'
import { useModal } from '@ebay/nice-modal-react'
import { useFormik } from 'formik'
import {
  BooleanParam,
  StringParam,
  useQueryParams,
  withDefault,
} from 'use-query-params'
import { Icon } from '@iconify/react'
import {
  Button,
  Dropdown,
  Input,
  useContentRefreshDomains,
  useGlobalState,
  useMessage,
} from '@pepper/ui'

import LoadingBar from 'components/LoadingBar'

import { useGetRelatedCategories } from 'utils/hooks/content-ideas/useGetRelatedCategories'
import { useCreateCategory } from 'utils/hooks/content-ideas/useCreateCategory'
import { useKeywordCategories } from 'container/SEOTools/hooks/useKeywordCategories'

import searchSchema from './schema'

import s from './Search.module.css'

type Props = {
  companyId: string
  domainId?: string
  hasNoDomainsInOrg?: boolean
}

const Search = ({ companyId, domainId, hasNoDomainsInOrg }: Props) => {
  const { permissions } = useGlobalState()
  const { showError, showInfo } = useMessage()
  const { show: showAddDomainModal } = useModal('add-domain')
  const { show: showUpsellBanner } = useModal('buy-cmp')
  const [isOpen, setIsOpen] = useState(false)
  const [queryParams, setQueryParams] = useQueryParams({
    step: withDefault(StringParam, undefined),
    query: withDefault(StringParam, undefined),
    category_id: withDefault(StringParam, undefined),
    content_ideas: withDefault(BooleanParam, false),
    retry_category: withDefault(StringParam, undefined),
  })

  const { data: categories } = useKeywordCategories({
    companyId,
    domainId,
    enabled: !!companyId && queryParams.content_ideas,
  })

  const { data: domains, refetch: handleRefetchDomains } =
    useContentRefreshDomains(companyId)

  const activeCategoryName = categories?.data?.find(
    category => category.id === queryParams.category_id
  )?.category
  const showRelatedSearches = !!queryParams.category_id && !!domainId
  const hasCMPPermission = permissions?.cmp

  const { data: relatedCategories, isLoading } = useGetRelatedCategories({
    companyId,
    domainId,
    category: activeCategoryName,
    options: {
      enabled: showRelatedSearches && !!activeCategoryName && !!domainId,
    },
  })

  const { mutateAsync: createCategory, isLoading: isCreating } =
    useCreateCategory()

  const currentlyLoadingCategory = localStorage.getItem(
    'currently_loading_category'
  )

  const handleDiscoverIdeasClick = async (values: { query: string }) => {
    if (!hasCMPPermission) {
      showUpsellBanner()
      return
    }
    if (domains.length === 0) {
      showAddDomainModal({
        refetch: handleRefetchDomains,
      })
      return
    }

    if (!domainId) {
      showInfo('Please select a domain.')
      return
    }
    const category = values.query.trim()
    setQueryParams({
      step: 'themes',
      category_id: undefined,
    })
    localStorage.setItem('currently_loading_category', category)
    try {
      const response = await createCategory({
        category,
        domainId,
        companyId,
      })
      const _currentlyLoadingCategory = localStorage.getItem(
        'currently_loading_category'
      )
      // Checking latest value of current step
      const currentStep = new URLSearchParams(window.location.search).get(
        'step'
      )
      if (
        response.data &&
        _currentlyLoadingCategory === category &&
        currentStep === 'themes'
      ) {
        setQueryParams({
          category_id: response.data.id,
        })
      }
    } catch (error) {
      // Used to retry category creation on <Themes> Error State
      setQueryParams(
        {
          retry_category: category,
        },
        'replaceIn'
      )
      showError(error?.response?.data?.message)
    }
    localStorage.removeItem('currently_loading_category')
  }

  const formik = useFormik({
    initialValues: {
      query: activeCategoryName ?? currentlyLoadingCategory ?? '',
    },
    validationSchema: searchSchema,
    onSubmit: handleDiscoverIdeasClick,
    enableReinitialize: true,
    validateOnMount: true,
  })

  const isGeneratingCategories =
    (isCreating || !!currentlyLoadingCategory) && queryParams.step === 'themes'

  useEffect(() => {
    if (!queryParams.step && formik.values.query) {
      formik.setFieldValue('query', '')
    }
  }, [queryParams.step])

  useEffect(() => {
    return () => {
      localStorage.removeItem('currently_loading_category')
    }
  }, [])

  const relatedSearchesMenu = (
    <div className={s.relatedSearchDropdown}>
      {relatedCategories && relatedCategories.length > 0 && !isLoading ? (
        <>
          <div className={s.heading}>
            {relatedCategories.length} Related Searches for &apos;
            {activeCategoryName}
            &apos;
          </div>
          <div className={s.results}>
            {relatedCategories.map(category => (
              <button
                className={s.menuBtn}
                key={category.category}
                onClick={() => {
                  formik.setFieldValue('query', category.category)
                  setIsOpen(false)
                }}
              >
                <Icon
                  icon="lucide:arrow-up-right"
                  width={16}
                  height={16}
                  className={s.resultIcon}
                />
                {category.category}
              </button>
            ))}
          </div>
        </>
      ) : (
        <>
          <div className={s.loaderHeading}>
            Related Searches for &apos;{activeCategoryName}&apos;
          </div>
          <div className={s.loaderResults}>
            {Array.from({ length: 5 }).map((__, idx) => (
              <div className={s.loaderRow} key={idx}>
                <LoadingBar height={18} width={18} borderRadius={4} />
                <LoadingBar height={18} width={84} borderRadius={4} />
                <LoadingBar height={18} width={84} borderRadius={4} />
              </div>
            ))}
          </div>
        </>
      )}
    </div>
  )

  return (
    <form className={s.wrapper} onSubmit={formik.handleSubmit}>
      <div className={s.inputWrapper}>
        <Input
          className={s.searchInput}
          size="small"
          placeholder="Ex: Digital Marketing"
          disabled={isGeneratingCategories || hasNoDomainsInOrg}
          {...formik.getFieldProps('query')}
          prefix={
            <Icon
              icon="lucide:search"
              width={16}
              height={16}
              className={s.searchIcon}
            />
          }
        />
        {showRelatedSearches && (
          <Dropdown
            overlay={relatedSearchesMenu}
            visible={isOpen}
            onVisibleChange={setIsOpen}
            trigger={['click']}
          >
            <button className={s.relatedSearchBtn}>
              <span className={s.relatedSearchIcon}>
                <Icon icon="lucide:git-branch" width={16} height={16} />
              </span>
              <span className={s.relatedSearchInner}>
                Related Searches
                <Icon icon="lucide:chevron-down" width={16} height={16} />
              </span>
            </button>
          </Dropdown>
        )}
      </div>
      <Button
        type="primary"
        htmlType="submit"
        loading={isGeneratingCategories}
        disabled={
          !formik.isValid || isGeneratingCategories || hasNoDomainsInOrg
        }
        data-id="discover-ideas-content-ideas-button"
      >
        Discover Ideas
      </Button>
    </form>
  )
}

export default Search
